import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('AuthCallback: No session_id found in URL');
          navigate('/login', { 
            replace: true,
            state: { error: 'No session ID received from authentication provider.' }
          });
          return;
        }

        
        
        // Use our backend as a proxy to exchange session_id (avoids CORS issues)
        const API = process.env.REACT_APP_BACKEND_URL;
        const isCrossOrigin = (() => {
          try { return new URL(API).origin !== window.location.origin; }
          catch { return false; }
        })();
        const fetchHeaders = { 'Content-Type': 'application/json' };
        const savedToken = localStorage.getItem('auth_token');
        if (savedToken) fetchHeaders['Authorization'] = `Bearer ${savedToken}`;

        const response = await fetch(`${API}/api/auth/google-exchange`, {
          method: 'POST',
          headers: fetchHeaders,
          credentials: isCrossOrigin ? 'omit' : 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          let errorMsg = 'Authentication failed. Please try again.';
          try {
            const errorData = await response.json();
            errorMsg = errorData.detail || errorMsg;
          } catch {
            // Response body not readable — use default message
          }
          console.error('AuthCallback: Exchange failed:', response.status);
          throw new Error(errorMsg);
        }

        const result = await response.json();
        
        // Store access token for cross-origin auth (Bearer token fallback)
        if (result.access_token) {
          localStorage.setItem('auth_token', result.access_token);
        }
        
        // Check if user needs onboarding (new user)
        if (result.is_new_user) {
          navigate('/onboarding', { 
            replace: true, 
            state: { user: result.user }
          });
        } else {
          navigate('/app', { 
            replace: true, 
            state: { user: result.user }
          });
        }
      } catch (error) {
        console.error('AuthCallback: Error:', error);
        navigate('/login', { 
          replace: true, 
          state: { error: error.message || 'Authentication failed. Please try again.' }
        });
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen cyber-bg flex items-center justify-center">
      <div className="text-center">
        <div className="relative mb-6">
          <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
          <Sparkles className="w-6 h-6 text-[#CCFF00] absolute -top-1 -right-1 animate-pulse" />
        </div>
        <h2 className="text-2xl font-outfit font-bold text-white mb-2">
          Setting up your space...
        </h2>
        <p className="text-white/60">
          Just a moment while we prepare everything
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
